import { useEffect, useMemo, useState, type ComponentProps } from "react";
import { Form, Input, Button, Row, Col, Select } from "antd";
import { useTranslation } from "react-i18next";
import {
  MdMailOutline,
  MdOutlineFormatListBulleted,
  MdPersonOutline,
  MdLocationOn,
  MdPhone,
} from "react-icons/md";
import Navbar from "../Menu/Navbar";
import Footer from "./Sections/Footer";
import PageHeaderAllPage from "../../Components/layouts/PageHeaderAllPage";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../apis/store";
import { toContactus } from "../../apis/actions/auth.actions";
import {
  toGetContactCities,
  toGetContactCountries,
  toGetContactTags,
} from "../../apis/actions/contactUs.actions";
import { clearContactCities } from "../../apis/slices/contactUsSlice";
import { toGetBrands, toGetModel } from "../../apis/actions/vehicle.actions";
import { showNotitfication } from "../../utils/Utils";
import { useNavigate } from "react-router-dom";

const BLOCKED_COUNTRIES = new Set(["Israel", "إسرائيل"]);
const MODEL_YEAR_START = 1990;
const listIcon = <MdOutlineFormatListBulleted size={17} />;

const toSelectOptions = (items: unknown[]) =>
  (Array.isArray(items) ? items : [])
    .map((item) => {
      if (typeof item === "string" || typeof item === "number") {
        return { label: String(item), value: item };
      }
      const record = (item ?? {}) as Record<string, unknown>;
      const label = String(
        record.name ??
          record.display_name ??
          record.label ??
          record.city ??
          record.country ??
          record.id ??
          ""
      );
      const value = record.id ?? record.value ?? record.name ?? label;
      return { label, value };
    })
    .filter((option) => option.label && option.value !== "" && option.value != null);

const ContactSelect = ({
  disabled,
  className,
  ...props
}: ComponentProps<typeof Select>) => (
  <Select
    size="large"
    showSearch
    allowClear
    optionFilterProp="label"
    virtual={false}
    className={["ac-contact-select", className].filter(Boolean).join(" ")}
    popupClassName="ac-contact-select-dropdown"
    prefix={listIcon}
    disabled={disabled}
    getPopupContainer={(node) => node.parentNode as HTMLElement}
    {...props}
  />
);

const ContactUs = () => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const isArabic = i18n.language.startsWith("ar");

  const countryId = Form.useWatch("country_id", form);
  const manufacturerId = Form.useWatch("manufacturer_id", form);

  const { countries, cities, tags } = useSelector(
    (state: RootState) => state.contactUs
  );
  const brands = useSelector((state: RootState) => state.vehicle.brands);
  const models = useSelector((state: RootState) => state.vehicle.models);
  const { profil } = useSelector((state: RootState) => state.login);
  const partnerId = profil?.data?.id ?? profil?.data?.partner_id;

  const countryOptions = useMemo(
    () =>
      toSelectOptions(countries.list).filter(
        (option) => !BLOCKED_COUNTRIES.has(option.label)
      ),
    [countries.list]
  );

  const cityOptions = useMemo(
    () => toSelectOptions(cities.list),
    [cities.list]
  );

  const manufacturerOptions = useMemo(
    () => toSelectOptions(brands?.list || []),
    [brands?.list]
  );

  const tagOptions = useMemo(() => toSelectOptions(tags.list), [tags.list]);

  const modelOptions = useMemo(() => {
    if (!manufacturerId) return [];
    const brand = (brands?.list || []).find(
      (item: any) => item?.id === manufacturerId || item?.name === manufacturerId
    );
    const brandName = brand?.name;
    return toSelectOptions(
      (models?.list || []).filter((model: any) => {
        const modelBrand = model?.brand_id;
        return (
          modelBrand === manufacturerId ||
          modelBrand === brandName ||
          modelBrand === brand?.id ||
          modelBrand?.[0] === manufacturerId ||
          modelBrand?.[0] === brand?.id
        );
      })
    );
  }, [brands?.list, manufacturerId, models?.list]);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: currentYear - MODEL_YEAR_START + 1 }, (_, index) => {
      const year = currentYear - index;
      return { label: String(year), value: year };
    });
  }, []);

  useEffect(() => {
    dispatch(toGetContactCountries(i18n.language));
    dispatch(toGetContactTags(i18n.language));
    dispatch(toGetBrands(i18n.language));
    dispatch(toGetModel());
  }, [dispatch, i18n.language]);

  useEffect(() => {
    if (!countryId) {
      dispatch(clearContactCities());
      return;
    }
    dispatch(
      toGetContactCities({ countryId, lang: i18n.language })
    );
  }, [countryId, dispatch, i18n.language]);

  const onFinish = async () => {
    setLoading(true);
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      const { country_id, city_id, tag_ids, ...rest } = values;
      const selectedTagIds = Array.isArray(tag_ids) ? tag_ids : [];
      const selectedTagNames = tagOptions
        .filter((option) =>
          selectedTagIds.some(
            (id: unknown) =>
              id === option.value || String(id) === String(option.value)
          )
        )
        .map((option) => option.label);
      const success = (await dispatch(
        toContactus({
          ...rest,
          country: country_id,
          city: city_id,
          tags: selectedTagIds,
          subject: selectedTagNames.join(", "),
          ...(partnerId ? { partner_id: partnerId } : {}),
        })
      )) as any;
      const ContactUsResponsse = success?.payload?.data?.result;

      if (ContactUsResponsse?.code === 200) {
        showNotitfication(
          i18n.language == "ar"
            ? " تم استلام رسالتكم بنجاح. نشكركم على تواصلكم معنا."
            : "Your message has been sent successfully . Thank you for contacting us!",
          "success"
        );
        setTimeout(() => {
          navigate("/");
        }, 2800);
      } else {
        showNotitfication(
          i18n.language == "ar"
            ? " عذرًا، حدث خطأ أثناء إرسال رسالتكم. يُرجى المحاولة مرة أخرى لاحقًا أو التواصل معنا عبر وسائل أخرى."
            : "Sorry, an error occurred. Please try again or contact us through alternative channels.",
          "error"
        );
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar type="internal" />

      <main className="main">
        <PageHeaderAllPage
          tone="brand"
          showMenu
          current={t("menu.contactUs")}
          banner="assets/imgs/page-header/banner1.png"
          link="contact-us"
        />
        <section
          className="ac-contact-page"
          dir={isArabic ? "rtl" : "ltr"}
        >
          <div className="container ac-contact-page__container">
            <div className="ac-contact-card">
              <header className="ac-contact-card__header">
                <h2 className="ac-contact-card__title">
                  {t("contactUs.getInTouch")}
                </h2>
                <div className="ac-contact-card__meta">
                  <span>
                    <MdLocationOn size={18} />
                    {t("contactUs.nationalAddress")}
                  </span>
                  <a href="mailto:info@autochapeau.com">
                    <MdMailOutline size={18} />
                    info@autochapeau.com
                  </a>
                  <a href="tel:920008980" dir="ltr">
                    <MdPhone size={18} />
                    920008980
                  </a>
                </div>
              </header>

              <Form
                form={form}
                onFinish={onFinish}
                className="ac-contact-form"
                requiredMark={false}
                autoComplete="off"
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="name"
                      rules={[
                        {
                          required: true,
                          message: t("contactUs.messages.nameRequired"),
                        },
                      ]}
                    >
                      <Input
                        className="ac-contact-input"
                        size="large"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="none"
                        spellCheck={false}
                        placeholder={t("contactUs.placeholder.name")}
                        prefix={<MdPersonOutline size={17} />}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      name="email_from"
                      rules={[
                        {
                          required: true,
                          type: "email",
                          message: t("contactUs.messages.emailRequired"),
                        },
                      ]}
                    >
                      <Input
                        className="ac-contact-input"
                        size="large"
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        placeholder={t("contactUs.placeholder.email")}
                        prefix={<MdMailOutline size={17} />}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={8}>
                    <Form.Item
                      name="country_id"
                      rules={[
                        {
                          required: true,
                          message: t("contactUs.messages.countryRequired"),
                        },
                      ]}
                    >
                      <ContactSelect
                        placeholder={t("contactUs.placeholder.country")}
                        options={countryOptions}
                        loading={countries.status === "loading"}
                        onChange={() => {
                          form.setFieldValue("city_id", undefined);
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={8}>
                    <Form.Item
                      name="city_id"
                      rules={[
                        {
                          required: true,
                          message: t("contactUs.messages.cityRequired"),
                        },
                      ]}
                    >
                      <ContactSelect
                        placeholder={
                          countryId
                            ? t("contactUs.placeholder.city")
                            : t("contactUs.placeholder.cityNeedsCountry")
                        }
                        options={cityOptions}
                        loading={cities.status === "loading"}
                        disabled={!countryId}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={8}>
                    <Form.Item
                      name="phone"
                      rules={[
                        {
                          required: true,
                          message: t("contactUs.messages.phoneRequired"),
                        },
                      ]}
                    >
                      <Input
                        className="ac-contact-input"
                        size="large"
                        inputMode="tel"
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        placeholder={t("contactUs.placeholder.phone")}
                        prefix={<MdPhone size={17} />}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={8}>
                    <Form.Item name="manufacturer_id">
                      <ContactSelect
                        placeholder={t("contactUs.placeholder.manufacturer")}
                        options={manufacturerOptions}
                        loading={brands?.status === "loading"}
                        onChange={() => {
                          form.setFieldValue("model_id", undefined);
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={8}>
                    <Form.Item name="model_id">
                      <ContactSelect
                        placeholder={
                          manufacturerId
                            ? t("contactUs.placeholder.modelName")
                            : t("contactUs.placeholder.modelNeedsManufacturer")
                        }
                        options={modelOptions}
                        loading={models?.status === "loading"}
                        disabled={!manufacturerId}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={8}>
                    <Form.Item name="model_year">
                      <ContactSelect
                        placeholder={t("contactUs.placeholder.modelYear")}
                        options={yearOptions}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Form.Item
                      name="tag_ids"
                      rules={[
                        {
                          required: true,
                          type: "array",
                          min: 1,
                          message: t("contactUs.messages.subjectRequired"),
                        },
                      ]}
                    >
                      <ContactSelect
                        mode="multiple"
                        maxTagCount="responsive"
                        className="ac-contact-select--multiple"
                        placeholder={t("contactUs.placeholder.subject")}
                        options={tagOptions}
                        loading={tags.status === "loading"}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Form.Item
                      name="description"
                      rules={[
                        {
                          required: true,
                          message: t("contactUs.messages.messageRequired"),
                        },
                      ]}
                    >
                      <Input.TextArea
                        className="ac-contact-input ac-contact-textarea"
                        rows={5}
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        placeholder={t("contactUs.placeholder.message")}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Form.Item>
                      <Button
                        disabled={loading}
                        htmlType="submit"
                        className="btn ac--btn-primary-jflatbold ac-contact-submit"
                      >
                        {loading ? t("btn.loading") : t("btn.send")}
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </div>
          </div>
        </section>
      </main>
      <Footer type="external" />
    </>
  );
};

export default ContactUs;
